/** How often ClinicalHUD re-ranks DDx / Pearls after the transcript changes. */

export const REFRESH_INTERVAL_SECONDS = Object.freeze([7, 12, 18, 24]);

export const DEFAULT_REFRESH_INTERVAL_SEC = 7;

export const REFRESH_INTERVAL_STORAGE_KEY = 'clinicalHud.refreshIntervalSec';

/** Phone/desktop list vs Even G2 lookalike canvas (preview only — not BLE). */

export const DISPLAY_MODE_PHONE = 'phone';

export const DISPLAY_MODE_SMART_GLASSES = 'smartGlasses';

export const DISPLAY_MODE_STORAGE_KEY = 'clinicalHud.displayMode';

/**
 * When true, Differential mode uses ChartMind's LLM path
 * (full transcript → generated Dx list). When false, SpecialtyKB retrieve-rank.
 * Default On.
 */
export const CHARTMIND_DDX_STORAGE_KEY = 'clinicalHud.chartMindDdx';

export const DEFAULT_CHARTMIND_DDX = true;

/**
 * ChartMind DDx engine when ChartMind DDx is On.
 * legacy → chartmind-diagnosis
 * upgraded → chartmind-diagnosis-upgraded
 * upgradedPlus → chartmind-diagnosis-upgraded-plus
 * Default legacy.
 */
export const DDX_ENGINE_LEGACY = 'legacy';

export const DDX_ENGINE_UPGRADED = 'upgraded';

export const DDX_ENGINE_UPGRADED_PLUS = 'upgradedPlus';

export const DDX_ENGINE_STORAGE_KEY = 'clinicalHud.ddxEngine';

export const DEFAULT_DDX_ENGINE = DDX_ENGINE_LEGACY;

export const DDX_ENGINE_OPTIONS = Object.freeze([
  DDX_ENGINE_LEGACY,
  DDX_ENGINE_UPGRADED,
  DDX_ENGINE_UPGRADED_PLUS,
]);

/**
 * When true, ClinicalHUD2 shows an Expert Advice toolbar shortcut that opens
 * OpenEvidence.com in a new tab. Default Off — we never store OpenEvidence credentials.
 */
export const EXPERT_ADVICE_STORAGE_KEY = 'clinicalHud.expertAdviceEnabled';

export const DEFAULT_EXPERT_ADVICE_ENABLED = false;

export function normalizeRefreshInterval(value) {
  const n = Number(value);
  return REFRESH_INTERVAL_SECONDS.includes(n) ? n : DEFAULT_REFRESH_INTERVAL_SEC;
}

export function readStoredRefreshInterval() {
  try {
    return normalizeRefreshInterval(
      window.localStorage.getItem(REFRESH_INTERVAL_STORAGE_KEY),
    );
  } catch {
    return DEFAULT_REFRESH_INTERVAL_SEC;
  }
}

export function writeStoredRefreshInterval(seconds) {
  const n = normalizeRefreshInterval(seconds);
  try {
    window.localStorage.setItem(REFRESH_INTERVAL_STORAGE_KEY, String(n));
  } catch {
    /* ignore quota / private mode */
  }
  return n;
}

export function normalizeDisplayMode(value) {
  return value === DISPLAY_MODE_SMART_GLASSES
    ? DISPLAY_MODE_SMART_GLASSES
    : DISPLAY_MODE_PHONE;
}

export function readStoredDisplayMode() {
  try {
    return normalizeDisplayMode(
      window.localStorage.getItem(DISPLAY_MODE_STORAGE_KEY),
    );
  } catch {
    return DISPLAY_MODE_PHONE;
  }
}

export function writeStoredDisplayMode(mode) {
  const next = normalizeDisplayMode(mode);
  try {
    window.localStorage.setItem(DISPLAY_MODE_STORAGE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeChartMindDdx(value) {
  if (value === false || value === 0 || value === '0' || value === 'false') {
    return false;
  }
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  return DEFAULT_CHARTMIND_DDX;
}

export function readStoredChartMindDdx() {
  try {
    const raw = window.localStorage.getItem(CHARTMIND_DDX_STORAGE_KEY);
    if (raw === null) return DEFAULT_CHARTMIND_DDX;
    return normalizeChartMindDdx(raw);
  } catch {
    return DEFAULT_CHARTMIND_DDX;
  }
}

export function writeStoredChartMindDdx(enabled) {
  const next = normalizeChartMindDdx(enabled);
  try {
    window.localStorage.setItem(CHARTMIND_DDX_STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeDdxEngine(value) {
  if (value === DDX_ENGINE_UPGRADED_PLUS || value === 'upgraded-plus') {
    return DDX_ENGINE_UPGRADED_PLUS;
  }
  if (value === DDX_ENGINE_UPGRADED) return DDX_ENGINE_UPGRADED;
  return DDX_ENGINE_LEGACY;
}

export function readStoredDdxEngine() {
  try {
    const raw = window.localStorage.getItem(DDX_ENGINE_STORAGE_KEY);
    if (raw === null) return DEFAULT_DDX_ENGINE;
    return normalizeDdxEngine(raw);
  } catch {
    return DEFAULT_DDX_ENGINE;
  }
}

export function writeStoredDdxEngine(engine) {
  const next = normalizeDdxEngine(engine);
  try {
    window.localStorage.setItem(DDX_ENGINE_STORAGE_KEY, next);
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeExpertAdviceEnabled(value) {
  if (value === true || value === 1 || value === '1' || value === 'true') {
    return true;
  }
  return DEFAULT_EXPERT_ADVICE_ENABLED;
}

export function readStoredExpertAdviceEnabled() {
  try {
    const raw = window.localStorage.getItem(EXPERT_ADVICE_STORAGE_KEY);
    if (raw === null) return DEFAULT_EXPERT_ADVICE_ENABLED;
    return normalizeExpertAdviceEnabled(raw);
  } catch {
    return DEFAULT_EXPERT_ADVICE_ENABLED;
  }
}

export function writeStoredExpertAdviceEnabled(enabled) {
  const next = normalizeExpertAdviceEnabled(enabled);
  try {
    window.localStorage.setItem(EXPERT_ADVICE_STORAGE_KEY, next ? '1' : '0');
  } catch {
    /* ignore quota / private mode */
  }
  return next;
}

export function normalizeHudTranscript(text) {
  return String(text || '').replace(/\s+/g, ' ').trim();
}

/**
 * True when the encounter text has changed enough to refresh DDx / Pearls.
 * The page interval is the time gate; this only ignores blank / identical text.
 */
export function shouldRefreshHudTranscript(lastAnalyzed, current) {
  const next = normalizeHudTranscript(current);
  if (!next) return false;
  return next !== normalizeHudTranscript(lastAnalyzed);
}
